// Recon List (Collect Cash before recon) — pure helpers (no React, no localization), so they can be
// unit-tested on their own. Shapes follow docs/partner-apis/collected-cash.md
// and recon-dbs.md in the citymall-services repo.

import { format } from "date-fns";

/** The server's error codes for the four collect-cash calls. */
export const CODE = {
  EXCEEDS: "COLLECTED_CASH_EXCEEDS_DELIVERED",
  MISMATCH: "COLLECTED_CASH_OTP_MISMATCH",
  EXPIRED: "COLLECTED_CASH_OTP_EXPIRED",
  LOCKED: "COLLECTED_CASH_OTP_LOCKED",
  TRIP_VERIFIED: "COLLECTED_CASH_TRIP_ALREADY_VERIFIED",
  RECON_CLOSED: "RECON_ALREADY_COMPLETED",
  DB_NOT_ACTIVE: "DB_NOT_ACTIVE",
  NOT_ENABLED: "COLLECT_CASH_NOT_ENABLED",
};

// Server error code → i18n key (ReconList/errors/*). Unknown: generic.
const ERROR_KEY = {
  [CODE.EXCEEDS]: "exceeds",
  [CODE.MISMATCH]: "mismatch",
  [CODE.EXPIRED]: "expired",
  [CODE.LOCKED]: "locked",
  [CODE.TRIP_VERIFIED]: "tripVerified",
  [CODE.RECON_CLOSED]: "reconClosed",
  [CODE.DB_NOT_ACTIVE]: "dbNotActive",
  [CODE.NOT_ENABLED]: "notEnabled",
  FORBIDDEN: "forbidden",
};

export function collectedCashErrorKey(code) {
  const key = ERROR_KEY[code];
  return key ? `ReconList/errors/${key}` : "ReconList/errorGeneric";
}

/** The OFD date query value for a picked day. */
export const toYmd = (date) => format(date, "yyyy-MM-dd");

/** ₹ amounts to two decimals, like the web page ("1500.00"). */
export const formatMoney = (n) => Number(n || 0).toFixed(2);

/**
 * "Name - 98xxxxxx10" (recon-dbs `db_text`) → { name, phone }. A text with no
 * separator is all name.
 */
export function splitDbText(text) {
  const raw = String(text || "").trim();
  const idx = raw.lastIndexOf(" - ");
  if (idx < 0) return { name: raw, phone: "" };
  return { name: raw.slice(0, idx).trim(), phone: raw.slice(idx + 3).trim() };
}

/**
 * The amount typed in the sheet → number, or null when it isn't a plain
 * amount (digits with up to two decimals).
 */
export function parseAmount(text) {
  const raw = String(text ?? "").trim();
  if (!/^\d+(\.\d{0,2})?$/.test(raw)) return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

/**
 * Why the amount can't be sent: "empty" | "invalid" | "zero" | "exceeds", or
 * null when it is fine. Mirrors the web modal's disabled rule
 * (`!amount || amount <= 0 || amount > remaining`).
 */
export function amountProblem(text, remaining) {
  const raw = String(text ?? "").trim();
  if (!raw) return "empty";
  const n = parseAmount(raw);
  if (n === null) return "invalid";
  if (n <= 0) return "zero";
  if (n > Number(remaining || 0)) return "exceeds";
  return null;
}

/**
 * Milliseconds the server clock is ahead of this device, from the
 * `server_time` a request/summary returned. Keeps the countdown honest on a
 * phone whose clock is off.
 */
export function clockOffsetMs(serverTime, nowMs = Date.now()) {
  if (!serverTime) return 0;
  const t = new Date(serverTime).getTime();
  return Number.isFinite(t) ? t - nowMs : 0;
}

/** Whole seconds until `expiresAt` on the server's clock, floored at 0. */
export function secondsLeft(expiresAt, offsetMs = 0, nowMs = Date.now()) {
  const end = new Date(expiresAt).getTime();
  if (!Number.isFinite(end)) return 0;
  return Math.max(0, Math.floor((end - (nowMs + offsetMs)) / 1000));
}

/** "m:ss" for the countdown. */
export function formatCountdown(seconds) {
  const s = Math.max(0, Number(seconds) || 0);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r < 10 ? "0" : ""}${r}`;
}

/**
 * The outcome of a request/verify call, whether it resolved (2xx, or a 400 /
 * 412 let through by the API module) or threw (anything else). Returns
 * `{ ok, status, code, data, message }`: `data` is the envelope's payload on
 * success and the error's extra fields (e.g. `attempts_left`, `remaining`)
 * otherwise.
 */
export function readHandledResponse(result) {
  const response = result?.response || result;
  const status = Number(response?.status) || 0;
  const body = response?.data || {};
  if (status >= 200 && status < 300) {
    return {
      ok: true,
      status,
      code: null,
      data: body.data ?? body,
      message: "",
    };
  }
  const code = body.error || body.code || null;
  // The server's fallback message is the code itself; that's not a toast.
  const message = body.message && body.message !== code ? body.message : "";
  return { ok: false, status, code, data: body.data || {}, message };
}

/** `attempts_left` from a 412 mismatch body, or null. */
export function attemptsLeftOf(outcome) {
  const n = outcome?.data?.attempts_left;
  return n === undefined || n === null ? null : Number(n);
}

/**
 * The summary's cap figures as numbers, with a zeroed default for a missing
 * block (the sheet's first render).
 */
export function normalizeSummary(summary) {
  const s = summary || {};
  return {
    enabled: s.enabled !== false,
    cash_deliverable: Number(s.cash_deliverable || 0),
    total_collected: Number(s.total_collected || 0),
    remaining: Number(s.remaining || 0),
    pending_request: s.pending_request || null,
    txns: Array.isArray(s.txns) ? s.txns : [],
  };
}

/** The list row after a verified handover, so the card updates before the reload. */
export function applyVerified(rows, dbId, result) {
  return (rows || []).map((row) =>
    row.db_id === dbId
      ? {
          ...row,
          cash_collected_early: Number(
            result?.total_collected ?? row.cash_collected_early ?? 0,
          ),
          pending_cash_request: null,
        }
      : row,
  );
}
